import React from "react";
import { Upload } from "lucide-react";
import { AppModal, AppModalHeader } from "../../../components/AppModal";
import { LoadingSpinner } from "../../../components/LoadingSpinner";
import { OfferImportSourcePicker, type OfferImportSource } from "./OfferImportSourcePicker";

type UglActionButtonProps = {
  offeringId: string;
  onImported?: () => void | Promise<void>;
  className?: string;
  label?: React.ReactNode;
  hideTrigger?: boolean;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
};

type ImportPayload = {
  error?: string;
  matched?: number;
  unmatched?: string[];
  components?: number;
};

export const UglActionButton: React.FC<UglActionButtonProps> = ({
  offeringId,
  onImported,
  className,
  label = "UGL",
  hideTrigger = false,
  open: openProp,
  onOpenChange,
}) => {
  const [uncontrolledOpen, setUncontrolledOpen] = React.useState(false);
  const open = openProp ?? uncontrolledOpen;
  const setOpen = (next: boolean) => {
    if (openProp === undefined) setUncontrolledOpen(next);
    onOpenChange?.(next);
  };
  const [busy, setBusy] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const [summary, setSummary] = React.useState<string | null>(null);
  const [busySource, setBusySource] = React.useState<OfferImportSource | null>(null);
  const fileInputRef = React.useRef<HTMLInputElement>(null);

  const close = (force = false) => {
    if (busy && !force) return;
    setOpen(false);
    setError(null);
    setSummary(null);
  };

  const runImport = async (input: File | OfferImportSource) => {
    setBusy(true);
    setError(null);
    setSummary(null);
    if (!(input instanceof File)) setBusySource(input);
    try {
      let res: Response;
      if (input instanceof File) {
        const fd = new FormData();
        fd.append("file", input);
        res = await fetch(`/api/ugl/offerings/${encodeURIComponent(offeringId)}/import`, {
          method: "POST",
          body: fd,
        });
      } else {
        res = await fetch(`/api/ugl/offerings/${encodeURIComponent(offeringId)}/import-from`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ source: input }),
        });
      }
      const raw = await res.text();
      let payload: ImportPayload = {};
      try {
        payload = raw ? (JSON.parse(raw) as ImportPayload) : {};
      } catch {
        payload = {};
      }
      if (!res.ok) {
        throw new Error(payload.error || raw.trim() || `UGL-Import fehlgeschlagen (${res.status})`);
      }
      const unmatched = payload.unmatched ?? [];
      const matched = payload.matched ?? 0;
      setSummary(
        unmatched.length > 0
          ? `${matched} zugeordnet, ${unmatched.length} ohne passende OZ (${unmatched.slice(0, 8).join(", ")}${unmatched.length > 8 ? " …" : ""}).`
          : `${matched} Positionen zugeordnet${payload.components ? ` · ${payload.components} Stücklistenzeilen` : ""}.`,
      );
      await onImported?.();
      if (unmatched.length === 0) close(true);
    } catch (e) {
      setError(e instanceof Error ? e.message : "UGL-Import fehlgeschlagen.");
    } finally {
      setBusy(false);
      setBusySource(null);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  return (
    <>
      {hideTrigger ? null : (
      <button
        type="button"
        className={className}
        onClick={() => {
          setOpen(true);
          setError(null);
          setSummary(null);
        }}
      >
        {label}
      </button>
      )}

      <AppModal
        open={open}
        onClose={() => close()}
        disableClose={busy}
        overlayClassName="z-[260]"
        panelClassName="md:max-w-lg"
        ariaLabelledBy="ugl-dialog-title"
      >
        <AppModalHeader titleId="ugl-dialog-title" title="UGL-Stückliste importieren" onClose={() => close()} closeDisabled={busy} />

        <div className="min-h-0 flex-1 overflow-y-auto px-5 py-4 space-y-4">
          <p className="text-sm text-app-text-muted">
            Hängt UGL-Unterartikel an vorhandene GAEB-Positionen (gleiche LV-Nummer). Verkaufspreise bleiben unverändert.
          </p>
          <button
            type="button"
            disabled={busy}
            onClick={() => fileInputRef.current?.click()}
            className="w-full text-left rounded-md border border-dashed border-sky-200 bg-app-surface-muted/60 px-4 py-5 hover:bg-sky-50/50 hover:border-sky-300 transition-colors disabled:opacity-60 disabled:pointer-events-none"
          >
            <div className="flex flex-col items-center gap-2 text-center">
              <div className="w-10 h-10 rounded-full bg-sky-100 flex items-center justify-center text-sky-600">
                {busy ? <LoadingSpinner size="sm" /> : <Upload className="w-5 h-5" />}
              </div>
              <p className="text-sm text-app-text-muted">
                Datei hier auswählen oder{" "}
                <span className="text-sky-700 font-semibold underline underline-offset-2">Datei wählen</span>
              </p>
              <p className="text-xs text-app-text-subtle">UGL 4.00 · .002 / .003 / .ugl (Max. 20 MB)</p>
            </div>
          </button>
          <OfferImportSourcePicker
            offeringId={offeringId}
            kind="ugl"
            disabled={busy}
            busySource={busySource}
            onPick={(source) => void runImport(source)}
          />
          {summary ? (
            <div className="rounded-md border border-app-border bg-app-surface-muted/60 px-3 py-2 text-sm text-app-text">
              {summary}
            </div>
          ) : null}
          {error ? (
            <div className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">{error}</div>
          ) : null}
        </div>

        <input
          ref={fileInputRef}
          type="file"
          className="hidden"
          accept=".ugl,.002,.003,.001,.004,.005,text/plain"
          onChange={(e) => {
            const f = e.target.files?.[0];
            if (f) void runImport(f);
          }}
        />
      </AppModal>
    </>
  );
};
